import React, { useEffect } from 'react';
import { BookingTheme, FLOW_DEFAULTS, loadThemeFonts } from './themes';
import { sanitizeUrl } from '@/utils/url';

/**
 * FlowLinkInBio — the public link-in-bio entry screen.
 *
 * Matched against the Claude Design prototype (linkinbio.jsx in
 * design-imports/). Do not change visual values without comparing
 * against the design source.
 */

// ─── Props ───────────────────────────────────────────────────────

export interface FlowLinkInBioProps {
  theme: BookingTheme;
  businessName: string;
  displayName?: string;
  handle?: string;
  bio?: string;
  logoUrl?: string | null;
  socialLinks?: Array<{ platform: string; url: string }>;
  customLinks?: Array<{ id: string; title: string; url: string; meta?: string }>;
  onBook: () => void;
  showBookButton?: boolean;
  /** Override the primary CTA label (default: "Book appointment") */
  bookButtonLabel?: string;
  /** Shown in place of the booking CTA (e.g. "Not accepting online bookings yet") */
  notice?: string;
  /** Fired when a custom link is clicked (e.g. analytics tracking) */
  onLinkClick?: (linkId: string) => void;
}

// ─── Social icon SVG paths (design: 16px stroke icons) ───────────

const SOCIAL_PATHS: Record<string, React.ReactNode> = {
  instagram: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" />
    </>
  ),
  tiktok: (
    <>
      <path d="M16 4v8.5a3.5 3.5 0 1 1-3.5-3.5" />
      <path d="M16 4c0 2 1.5 4 4 4" />
    </>
  ),
  threads: (
    <>
      <path d="M12 3c-4.5 0-7 2.7-7 7.5 0 5 2.5 9 8 9 3.5 0 6-2 6-5 0-2.5-2-4-5-4-1.5 0-3 .5-3 2 0 1 .8 1.7 2 1.7 1.5 0 2-1 2-2" />
    </>
  ),
  x: (
    <>
      <path d="M4 4l16 16" />
      <path d="M20 4L4 20" />
    </>
  ),
  pinterest: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M11 7c-2.5 0-4 1.7-4 4 0 1.3.7 2 1.3 2 .4 0 .7-.3.7-.7 0-.4-.3-.7-.3-1.3 0-1.5 1.3-2.7 3-2.7 1.6 0 2.7.9 2.7 2.3 0 1.8-1 3.4-2.3 3.4-.7 0-1.3-.6-1.1-1.4l.5-2c.1-.5-.2-.9-.7-.9-.9 0-1.6 1-1.6 2.3 0 .8.3 1.4.3 1.4l-1 4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18" />
      <path d="M12 3a14 14 0 0 0 0 18" />
    </>
  ),
};

function SocialIcon({ platform }: { platform: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {SOCIAL_PATHS[platform] || SOCIAL_PATHS.globe}
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3 7h8M7.5 3.5L11 7L7.5 10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────────

export function FlowLinkInBio({
  theme,
  businessName,
  displayName,
  handle,
  bio,
  logoUrl,
  socialLinks,
  customLinks,
  onBook,
  showBookButton = true,
  bookButtonLabel = 'Book appointment',
  notice,
  onLinkClick,
}: FlowLinkInBioProps) {
  useEffect(() => { loadThemeFonts(theme); }, [theme]);

  const t = {
    bg: theme.flowBg || FLOW_DEFAULTS.bg,
    surface: theme.flowSurface || FLOW_DEFAULTS.surface,
    ink: theme.flowInk || FLOW_DEFAULTS.ink,
    inkSoft: theme.flowInkSoft || FLOW_DEFAULTS.inkSoft,
    inkFaint: theme.flowInkFaint || FLOW_DEFAULTS.inkFaint,
    hairline: theme.flowHairline || FLOW_DEFAULTS.hairline,
    avatar: theme.libAvatar || theme.bg,
    avatarFg: theme.libAvatarFg || theme.fg,
    cta: theme.libCta || theme.bg,
    ctaFg: theme.libCtaFg || theme.fg,
    ring: theme.libAvatarRing || theme.flowAccentBorder,
    ringHalo: theme.libRingHalo || 'rgba(255,255,255,0.55)',
    figgy: theme.libFiggyColor || '#5956F6',
  };

  const nameToShow = displayName || businessName;
  const initial = nameToShow.trim().charAt(0).toUpperCase();

  const visibleSocials = (socialLinks || []).filter(link => sanitizeUrl(link.url));
  const visibleLinks = (customLinks || []).filter(link => sanitizeUrl(link.url));

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: t.bg,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Inter Tight", system-ui, sans-serif',
        color: t.ink,
      }}
    >
      {/* Body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          padding: '74px 24px 16px',
        }}
      >
        <div style={{ maxWidth: 380, margin: '0 auto' }}>
          {/* ── Profile block ─────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            {/* Avatar */}
            <div style={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              background: t.avatar,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow:
                '0 18px 30px rgba(0,0,0,0.14), ' +
                `0 0 0 6px ${t.ringHalo}, ` +
                `0 0 0 7px ${t.ring}`,
              position: 'relative',
              marginBottom: 18,
              overflow: 'hidden',
            }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={nameToShow}
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <span style={{
                  fontFamily: theme.display,
                  fontStyle: theme.displayItalic ? 'italic' : 'normal',
                  fontWeight: theme.displayWeight,
                  fontSize: 46,
                  lineHeight: 1,
                  color: t.avatarFg,
                  letterSpacing: '-0.02em',
                  transform: 'translateY(-1px)',
                }}>{initial}</span>
              )}
            </div>

            {/* Name */}
            <div style={{
              fontFamily: theme.display,
              fontStyle: theme.displayItalic ? 'italic' : 'normal',
              fontWeight: theme.displayWeight,
              fontSize: 34,
              lineHeight: 1.08,
              letterSpacing: '-0.015em',
              color: t.ink,
              textWrap: 'balance' as const,
            }}>{nameToShow}</div>

            {/* Handle */}
            {handle && (
              <div style={{
                marginTop: 8,
                fontSize: 12.5,
                color: t.inkSoft,
                letterSpacing: '-0.005em',
                fontVariantNumeric: 'tabular-nums',
              }}>{handle}</div>
            )}

            {/* Bio */}
            {bio && (
              <div style={{
                marginTop: 14,
                fontSize: 14,
                lineHeight: 1.5,
                color: t.inkSoft,
                letterSpacing: '-0.005em',
                maxWidth: 290,
                textWrap: 'pretty' as const,
              }}>{bio}</div>
            )}

            {/* Socials */}
            {visibleSocials.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 18 }}>
                {visibleSocials.map((link) => (
                  <a
                    key={link.platform}
                    href={sanitizeUrl(link.url)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.platform}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      border: `0.5px solid ${t.hairline}`,
                      background: t.surface,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: t.ink,
                      textDecoration: 'none',
                    }}
                  >
                    <SocialIcon platform={link.platform} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* ── Primary booking CTA ───────────────────────── */}
          {showBookButton && (
            <button
              onClick={onBook}
              style={{
                marginTop: 28,
                width: '100%',
                height: 60,
                borderRadius: 18,
                border: 'none',
                background: t.cta,
                color: t.ctaFg,
                fontFamily: 'inherit',
                fontSize: 16,
                fontWeight: 500,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 22px',
                boxShadow: '0 10px 22px rgba(0,0,0,0.18), 0 1px 0 rgba(255,255,255,0.16) inset, 0 -1px 0 rgba(0,0,0,0.10) inset',
                WebkitTapHighlightColor: 'transparent',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
                <CalendarIcon />
                {bookButtonLabel}
              </span>
              <ArrowIcon />
            </button>
          )}

          {/* ── Notice (when booking is unavailable) ──────── */}
          {!showBookButton && notice && (
            <div style={{
              marginTop: 28,
              width: '100%',
              background: t.surface,
              border: `0.5px solid ${t.hairline}`,
              borderRadius: 16,
              padding: '14px 18px',
              boxShadow: FLOW_DEFAULTS.cardShadow,
              fontSize: 13,
              lineHeight: 1.5,
              color: t.inkSoft,
              letterSpacing: '-0.005em',
              textAlign: 'center' as const,
            }}>{notice}</div>
          )}

          {/* ── Featured links ────────────────────────────── */}
          {visibleLinks.length > 0 && (
            <>
              <div style={{
                marginTop: 30,
                marginBottom: 12,
                fontSize: 10,
                textTransform: 'uppercase' as const,
                letterSpacing: '0.22em',
                color: t.inkFaint,
                fontWeight: 600,
                textAlign: 'left',
              }}>Featured links</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visibleLinks.map((link) => (
                  <a
                    key={link.id}
                    href={sanitizeUrl(link.url)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onLinkClick?.(link.id)}
                    style={{
                      textAlign: 'left',
                      background: t.surface,
                      border: `0.5px solid ${t.hairline}`,
                      boxShadow: FLOW_DEFAULTS.cardShadow,
                      padding: '14px 18px 16px',
                      borderRadius: 16,
                      cursor: 'pointer',
                      color: t.ink,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      textDecoration: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      transition: 'background 220ms ease, transform 180ms ease',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: 14.5,
                        fontWeight: 500,
                        color: t.ink,
                        letterSpacing: '-0.005em',
                      }}>{link.title}</div>
                      {link.meta && (
                        <div style={{
                          marginTop: 3,
                          fontSize: 12,
                          color: t.inkSoft,
                          letterSpacing: '-0.005em',
                        }}>{link.meta}</div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, opacity: 0.55 }}>
                      <ArrowIcon />
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}

          {/* ── Footer: Powered by Figgy + legal ──────────── */}
          <div style={{
            marginTop: 36,
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            fontSize: 11,
            color: t.inkFaint,
            letterSpacing: '-0.005em',
          }}>
            <span>Powered by</span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: t.figgy,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}>
              <span style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2.5,
                background: t.figgy,
              }} />
              Figgy
            </span>
          </div>
          <div style={{
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontSize: 10.5,
            color: t.inkFaint,
            letterSpacing: '-0.005em',
          }}>
            <a href="/legal/terms-and-conditions" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms</a>
            <a href="/legal/privacy-policy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy</a>
          </div>
        </div>
      </div>
    </div>
  );
}
